<script setup lang="ts">
/**
 * Modale « Sélectionner un collaborateur » — module Matériel collaborateur V2.
 *
 * Un collaborateur EST un User existant : cette modale propose une recherche
 * d'utilisateurs (API) et émet l'identité choisie au parent. La création de
 * collaborateur « from scratch » n'existe plus.
 *
 * Pattern modale natif `<dialog class="modal">` DaisyUI — cf. OnbHandoverDialog.vue.
 * Le parent récupère un `ref` et appelle `open()` (exposé via defineExpose).
 */
import { onBeforeUnmount, ref, watch } from 'vue'
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/vue/24/outline'
import { useOnboardingStore } from '@/v2/stores/useOnboardingStore'
import type { ApiCollaboratorIdentity } from '@/api/materialApi'

const emit = defineEmits<{
  selected: [user: ApiCollaboratorIdentity]
}>()

const store = useOnboardingStore()

const dialog = ref<HTMLDialogElement | null>(null)

/* -------------------------------------------------------------------------- */
/* État de la recherche                                                       */
/* -------------------------------------------------------------------------- */

const searchTerm = ref('')
const results = ref<ApiCollaboratorIdentity[]>([])
const searching = ref(false)
const searched = ref(false)

let debounceTimer: ReturnType<typeof setTimeout> | null = null
let searchToken = 0

/** Nom affichable d'une identité (repli sur l'email puis l'uuid). */
function identityName(user: ApiCollaboratorIdentity): string {
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim()
  return name || user.email || user.userUuid
}

async function runSearch(term: string): Promise<void> {
  const token = ++searchToken
  searching.value = true
  try {
    const rows = await store.searchUsers(term)
    if (token !== searchToken) return // réponse obsolète
    results.value = rows
  } catch {
    if (token !== searchToken) return
    results.value = []
  } finally {
    if (token === searchToken) {
      searching.value = false
      searched.value = true
    }
  }
}

// Debounce léger (~300 ms) sur la saisie de recherche.
watch(searchTerm, (value) => {
  if (debounceTimer) clearTimeout(debounceTimer)
  const term = value.trim()
  if (!term) {
    results.value = []
    searched.value = false
    searching.value = false
    searchToken++
    return
  }
  debounceTimer = setTimeout(() => void runSearch(term), 300)
})

onBeforeUnmount(() => {
  if (debounceTimer) clearTimeout(debounceTimer)
})

/* -------------------------------------------------------------------------- */
/* Ouverture / fermeture                                                      */
/* -------------------------------------------------------------------------- */

function reset(): void {
  if (debounceTimer) clearTimeout(debounceTimer)
  searchTerm.value = ''
  results.value = []
  searching.value = false
  searched.value = false
  searchToken++
}

/** Ouvre la modale (API publique consommée par le parent). */
function open(): void {
  reset()
  dialog.value?.showModal()
}

function close(): void {
  dialog.value?.close()
}

defineExpose({ open })

/* -------------------------------------------------------------------------- */
/* Sélection                                                                  */
/* -------------------------------------------------------------------------- */

function select(user: ApiCollaboratorIdentity): void {
  emit('selected', user)
  close()
}

const titleId = 'onb-select-collaborator-title'
</script>

<template>
  <dialog ref="dialog" class="modal" :aria-labelledby="titleId">
    <div class="modal-box w-full max-w-2xl max-h-[90dvh] overflow-y-auto p-4 sm:p-6">
      <!-- En-tête + fermeture -->
      <div class="flex items-start justify-between gap-4">
        <div>
          <h2 :id="titleId" class="text-lg font-semibold">Sélectionner un collaborateur</h2>
          <p class="text-sm text-base-content/70">
            Recherchez l'utilisateur à qui affecter du matériel.
          </p>
        </div>
        <button
          type="button"
          class="btn btn-ghost btn-sm motion-reduce:transition-none"
          aria-label="Fermer"
          @click="close"
        >
          <XMarkIcon class="h-5 w-5" aria-hidden="true" />
        </button>
      </div>

      <!-- Recherche -->
      <div class="mt-4">
        <label class="label" for="onb-select-search">
          <span class="label-text">Rechercher un collaborateur</span>
        </label>
        <label class="input input-bordered flex items-center gap-2">
          <MagnifyingGlassIcon class="h-5 w-5 text-base-content/80" aria-hidden="true" />
          <input
            id="onb-select-search"
            v-model="searchTerm"
            type="search"
            class="grow"
            placeholder="Nom, prénom ou email…"
            autocomplete="off"
          />
        </label>

        <!-- État chargement -->
        <p v-if="searching" class="mt-3 text-sm text-base-content/80" aria-live="polite">
          Recherche en cours…
        </p>

        <!-- Résultats -->
        <ul
          v-else-if="results.length"
          role="list"
          class="mt-3 divide-y divide-base-300 overflow-hidden rounded-field border border-base-300"
        >
          <li v-for="user in results" :key="user.userUuid">
            <button
              type="button"
              class="flex w-full flex-col items-start gap-0.5 p-3 text-left transition-colors hover:bg-base-200 motion-reduce:transition-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
              @click="select(user)"
            >
              <span class="font-semibold break-words">{{ identityName(user) }}</span>
              <span class="text-sm break-words text-base-content/80">
                {{ user.email || '—' }}
                <span v-if="user.jobTitle"> · {{ user.jobTitle }}</span>
              </span>
            </button>
          </li>
        </ul>

        <!-- Aucun résultat -->
        <p
          v-else-if="searched && searchTerm.trim()"
          class="mt-3 text-sm text-base-content/80"
          aria-live="polite"
        >
          Aucun collaborateur ne correspond à la recherche.
        </p>
      </div>

      <!-- Actions -->
      <div class="modal-action flex-wrap">
        <button type="button" class="btn btn-ghost motion-reduce:transition-none" @click="close">
          Annuler
        </button>
      </div>
    </div>

    <form method="dialog" class="modal-backdrop">
      <button aria-label="Fermer">Fermer</button>
    </form>
  </dialog>
</template>
